import React, { useState, useEffect } from 'react';
import { API_LINK } from '../apiLink/apiLink';
import { menuConfig } from './menuConfig';

const Sidebar = ({
    isMobile,
    isSidebarOpen,
    hoveredItem,
    setHoveredItem,
    onNavigate,
    activePage,
    role = 'admin',
    userInfo = {},
    setIsSidebarOpen
}) => {
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const getInitialProfile = React.useCallback(() => {
        const cached = sessionStorage.getItem(`cached-profile-${role}`);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (parsed) {
                    if (role === 'student') {
                        return {
                            name: parsed.name || userInfo?.name || 'Student User',
                            subtext1: '',
                            subtext2: parsed.studentId || userInfo?.id || 'SVU2024001',
                            profileImage: parsed.profileImage || null
                        };
                    } else if (role === 'teacher') {
                        return {
                            name: parsed.name || userInfo?.name || 'Teacher User',
                            subtext1: parsed.designation || userInfo?.designation || 'Lecturer',
                            subtext2: parsed.department || userInfo?.department || 'Computer Science',
                            profileImage: parsed.profileImage || null
                        };
                    } else if (role === 'librarian') {
                        return {
                            name: parsed.name || userInfo?.name || 'Library User',
                            subtext1: '',
                            subtext2: parsed.libraryId || userInfo?.id || 'LIB2024001',
                            profileImage: parsed.profileImage || null
                        };
                    }
                }
            } catch (e) {
                console.error('Error parsing cached profile:', e);
            }
        }

        if (role === 'admin') {
            return {
                name: userInfo?.name || 'Administrator',
                subtext1: 'System Administrator',
                subtext2: 'Administration',
                profileImage: null
            };
        } else if (role === 'examcell') {
            return {
                name: userInfo?.name || 'Exam Cell Officer',
                subtext1: userInfo?.designation || 'Exam Officer',
                subtext2: 'Examination Cell',
                profileImage: null
            };
        } else if (role === 'hod') {
            return {
                name: userInfo?.name || 'HOD',
                subtext1: 'Head of Department',
                subtext2: userInfo?.department || 'Department',
                profileImage: userInfo?.profileImage || null
            };
        } else if (role === 'accounts') {
            return {
                name: userInfo?.name || 'Accounts User',
                subtext1: userInfo?.designation || 'Accounts Officer',
                subtext2: userInfo?.department || 'Accounts Department',
                profileImage: userInfo?.profileImage || null
            };
        } else if (role === 'parent') {
            return {
                name: userInfo?.name || 'Parent User',
                subtext1: userInfo?.relation || 'Parent',
                subtext2: userInfo?.parentId || 'PARENT-001',
                profileImage: userInfo?.profileImage || null
            };
        } else if (role === 'student') {
            return {
                name: userInfo?.name || 'Student User',
                subtext1: '',
                subtext2: userInfo?.id || 'SVU2024001',
                profileImage: userInfo?.profileImage || null
            };
        } else if (role === 'teacher') {
            return {
                name: userInfo?.name || 'Teacher User',
                subtext1: userInfo?.designation || 'Lecturer',
                subtext2: userInfo?.department || 'Computer Science',
                profileImage: userInfo?.profileImage || null
            };
        } else if (role === 'librarian') {
            return {
                name: userInfo?.name || 'Library User',
                subtext1: '',
                subtext2: userInfo?.id || 'LIB2024001',
                profileImage: userInfo?.profileImage || null
            };
        }

        return {
            name: 'User',
            subtext1: '',
            subtext2: '',
            profileImage: null
        };
    }, [role, userInfo]);

    const [profile, setProfile] = useState(getInitialProfile);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const sidebarRef = React.useRef(null);

    // Sync profile state when role or userInfo updates
    useEffect(() => {
        setProfile(getInitialProfile());
    }, [role, userInfo, getInitialProfile]);

    // Handle image loaded caching to prevent image load flicker
    useEffect(() => {
        if (profile.profileImage) {
            setImageError(false);
            const isLoaded = sessionStorage.getItem(`img-loaded-${profile.profileImage}`) === 'true';
            setImageLoading(!isLoaded);
        } else {
            setImageLoading(false);
        }
    }, [profile.profileImage]);

    // Restore scroll position on mount
    useEffect(() => {
        const savedScrollTop = sessionStorage.getItem(`sidebar-scroll-${role}`);
        if (savedScrollTop && sidebarRef.current) {
            sidebarRef.current.scrollTop = parseInt(savedScrollTop, 10);
        }
    }, [role]);

    // Save scroll position when user scrolls
    const handleScroll = (e) => {
        sessionStorage.setItem(`sidebar-scroll-${role}`, e.target.scrollTop);
    };

    // Dynamic data loading for Student, Teacher, and Librarian on mount
    useEffect(() => {
        const fetchDynamicProfile = async () => {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) {
                // Fallback to userInfo if no token
                if (role === 'student') {
                    setProfile({
                        name: userInfo?.name || 'Student User',
                        subtext1: '',
                        subtext2: userInfo?.id || 'SVU2024001',
                        profileImage: null
                    });
                } else if (role === 'teacher') {
                    setProfile({
                        name: userInfo?.name || 'Teacher User',
                        subtext1: userInfo?.designation || 'Lecturer',
                        subtext2: userInfo?.department || 'Computer Science',
                        profileImage: null
                    });
                } else if (role === 'librarian') {
                    setProfile({
                        name: userInfo?.name || 'Library User',
                        subtext1: '',
                        subtext2: userInfo?.id || 'LIB2024001',
                        profileImage: null
                    });
                }
                return;
            }

            try {
                if (role === 'student') {
                    const response = await fetch(`${API_LINK}/api/student/profile`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const result = await response.json();
                        if (result.success && result.data) {
                            sessionStorage.setItem(`cached-profile-${role}`, JSON.stringify(result.data));
                            setProfile({
                                name: result.data.name,
                                subtext1: '',
                                subtext2: result.data.studentId,
                                profileImage: result.data.profileImage
                            });
                        }
                    }
                } else if (role === 'teacher') {
                    const response = await fetch(`${API_LINK}/api/teacher/dashboard`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const result = await response.json();
                        if (result.success && result.data?.teacher) {
                            const t = result.data.teacher;
                            sessionStorage.setItem(`cached-profile-${role}`, JSON.stringify(t));
                            setProfile({
                                name: t.name,
                                subtext1: t.designation || 'Lecturer',
                                subtext2: t.department || 'Computer Science',
                                profileImage: t.profileImage
                            });
                        }
                    }
                } else if (role === 'librarian') {
                    const response = await fetch(`${API_LINK}/api/library/profile`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const result = await response.json();
                        if (result.success && result.data) {
                            sessionStorage.setItem(`cached-profile-${role}`, JSON.stringify(result.data));
                            setProfile({
                                name: result.data.name,
                                subtext1: '',
                                subtext2: result.data.libraryId,
                                profileImage: result.data.profileImage
                            });
                        }
                    }
                }
            } catch (err) {
                console.error(`Error loading unified profile for ${role}:`, err);
            }
        };

        if (['student', 'teacher', 'librarian'].includes(role)) {
            fetchDynamicProfile();
        }
    }, [role]);

    // Handle logout confirm
    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            fetch(`${API_LINK}/api/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }).catch(() => {
                // Silently ignore logout fetch error
            });
        }

        if (onNavigate) {
            onNavigate(role === 'examcell' ? 'login' : 'home');
        } else {
            window.location.href = '/';
        }
    };

    // Handle navigation
    const handleNavigation = (item) => {
        if (item.path === 'logout' || item.path === 'login' || item.label === 'Logout') {
            setShowLogoutConfirm(true);
        } else if (onNavigate) {
            if (setIsSidebarOpen && isMobile) {
                setIsSidebarOpen(false);
            }
            onNavigate(role === 'parent' ? item.label : item.path);
        }
    };

    // Get initials
    const getUserInitials = (name) => {
        if (!name || typeof name !== 'string') return role.substring(0, 2).toUpperCase();
        const names = name.trim().split(' ');
        if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getProfileImageUrl = () => {
        if (profile.profileImage) {
            return `${API_LINK}${profile.profileImage}`;
        }
        return null;
    };

    const profileImageUrl = getProfileImageUrl();
    const showImage = profileImageUrl && !imageError;
    const menuItems = menuConfig[role] || [];

    return (
        <aside
            ref={sidebarRef}
            onScroll={handleScroll}
            className="sidebar-container"
            style={{
                width: isMobile ? '200px' : '280px',
                background: '#ffffff',
                padding: isMobile ? '0.4rem 0.5rem' : '0.6rem 0.8rem',
                position: 'fixed',
                left: isMobile ? (isSidebarOpen ? '0' : '-200px') : '0',
                top: 0,
                height: '100vh',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                zIndex: 1001,
                overflowY: 'auto',
                boxSizing: 'border-box'
            }}
        >
            <style>
                {`
                    .sidebar-container {
                        scrollbar-width: thin;
                        scrollbar-color: rgba(35, 133, 72, 0.2) transparent;
                    }
                    .sidebar-container::-webkit-scrollbar {
                        width: 5px;
                    }
                    .sidebar-container::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .sidebar-container::-webkit-scrollbar-thumb {
                        background: rgba(35, 133, 72, 0.2);
                        border-radius: 10px;
                        transition: background 0.3s ease;
                    }
                    .sidebar-container::-webkit-scrollbar-thumb:hover {
                        background: rgba(35, 133, 72, 0.45);
                    }
                    @keyframes profileShimmer {
                        0% { background-position: -200% 0; }
                        100% { background-position: 200% 0; }
                    }
                `}
            </style>

            {/* User Info */}
            <div style={{
                textAlign: 'center',
                padding: isMobile ? '0.2rem 0' : '0.6rem 0',
                borderBottom: '2px solid #ecf0f1',
                marginBottom: isMobile ? '0.3rem' : '0.6rem',
                marginTop: isMobile ? '2.5rem' : '0.5rem',
                flexShrink: 0
            }}>
                <div style={{
                    width: isMobile ? '40px' : '64px',
                    height: isMobile ? '40px' : '64px',
                    margin: isMobile ? '0 auto 0.2rem' : '0 auto 0.6rem',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '2.5px solid #97d6ac',
                    transition: 'all 0.3s ease',
                    backgroundColor: '#238548',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? '0.9rem' : '1.4rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    flexShrink: 0,
                    position: 'relative',
                    ...(showImage && imageLoading ? {
                        background: 'linear-gradient(90deg, #1e7a3e 25%, #2ea960 50%, #1e7a3e 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'profileShimmer 1.5s ease-in-out infinite'
                    } : {})
                }}>
                    {showImage && (
                        <img
                            src={profileImageUrl}
                            alt={profile.name}
                            onLoad={() => {
                                setImageLoading(false);
                                if (profile.profileImage) {
                                    sessionStorage.setItem(`img-loaded-${profile.profileImage}`, 'true');
                                }
                            }}
                            onError={() => {
                                setImageError(true);
                                setImageLoading(false);
                            }}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                opacity: imageLoading ? 0 : 1,
                                transition: 'opacity 0.3s ease'
                            }}
                        />
                    )}
                    {!showImage && getUserInitials(profile.name)}
                </div>
                <h3 style={{ margin: isMobile ? '0 0 0.15rem 0' : '0 0 0.3rem 0', color: '#333', fontSize: isMobile ? '0.85rem' : '1.1rem' }}>{profile.name}</h3>
                {profile.subtext1 && <p style={{ margin: isMobile ? '0 0 0.1rem 0' : '0 0 0.15rem 0', color: '#666', fontSize: isMobile ? '0.7rem' : '0.85rem' }}>{profile.subtext1}</p>}
                {profile.subtext2 && <p style={{ margin: 0, color: '#666', fontSize: isMobile ? '0.65rem' : '0.8rem' }}>{profile.subtext2}</p>}
            </div>

            {/* Sidebar Menu */}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {menuItems.map((item, index) => {
                    const isLogout = item.path === 'logout' || item.path === 'login' || item.label === 'Logout';
                    const isActive = role === 'parent' ? (item.label === activePage) : (item.path === activePage);

                    return (
                        <li key={index} style={{ marginBottom: isMobile ? '0.1rem' : '0.25rem' }}>
                            <div
                                role="button"
                                tabIndex={0}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    padding: isMobile ? '0.3rem 0.5rem' : '0.5rem 0.8rem',
                                    color: isLogout
                                        ? (hoveredItem === 'Logout' ? '#ffffff' : '#dc2626')
                                        : (isActive || hoveredItem === item.label) ? '#ffffff' : '#333',
                                    borderRadius: isMobile ? '4px' : '6px',
                                    transition: 'all 0.3s ease',
                                    background: isLogout
                                        ? (hoveredItem === 'Logout' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'rgba(239, 68, 68, 0.05)')
                                        : (isActive || hoveredItem === item.label) ? '#238548' : 'transparent',
                                    border: isLogout
                                        ? (hoveredItem === 'Logout' ? '1px solid transparent' : '1px solid rgba(239, 68, 68, 0.15)')
                                        : '1px solid transparent',
                                    boxShadow: isLogout && hoveredItem === 'Logout'
                                        ? '0 8px 20px rgba(239, 68, 68, 0.2)'
                                        : 'none',
                                    transform: isLogout
                                        ? (hoveredItem === 'Logout' ? 'scale(1.02) translateX(5px)' : 'none')
                                        : (isActive || hoveredItem === item.label) ? 'translateX(5px)' : 'translateX(0)',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    minHeight: isMobile ? '30px' : '40px',
                                    boxSizing: 'border-box',
                                    textAlign: 'left',
                                    ...(isLogout ? { marginTop: '0.8rem', borderTop: '1px solid #f0f0f0', paddingTop: isMobile ? '0.4rem' : '0.8rem' } : {})
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNavigation(item);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleNavigation(item);
                                    }
                                }}
                                onMouseEnter={() => {
                                    if (!isActive) {
                                        setHoveredItem(item.label);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setHoveredItem(null);
                                }}
                            >
                                <span style={{
                                    fontSize: isMobile ? '0.95rem' : '1.15rem',
                                    marginRight: isMobile ? '0.4rem' : '0.6rem',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {isLogout ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width={isMobile ? "14" : "18"}
                                            height={isMobile ? "14" : "18"}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: hoveredItem === 'Logout' ? 'translateX(2px)' : 'none'
                                            }}
                                        >
                                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" />
                                            <polyline points="16 17 21 12 16 7" stroke="currentColor" />
                                            <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" />
                                        </svg>
                                    ) : (
                                        item.icon
                                    )}
                                </span>
                                <span style={{
                                    flex: 1,
                                    lineHeight: '1.2',
                                    fontSize: isMobile ? '0.7rem' : '0.85rem',
                                    fontWeight: '500',
                                    textAlign: 'left'
                                }}>
                                    {item.label}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* Reusable Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.45)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        animation: 'fadeInOverlay 0.25s ease'
                    }}
                    onClick={() => setShowLogoutConfirm(false)}
                >
                    <style>{`
                        @keyframes fadeInOverlay {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        @keyframes slideUpModal {
                            from { opacity: 0; transform: translateY(30px) scale(0.95); }
                            to { opacity: 1; transform: translateY(0) scale(1); }
                        }
                        .logout-confirm-btn {
                            transition: all 0.2s ease !important;
                        }
                        .logout-confirm-btn:hover {
                            transform: translateY(-1px);
                            box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
                        }
                    `}</style>
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '2rem 2.5rem',
                            width: '380px',
                            maxWidth: '90vw',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                            textAlign: 'center',
                            animation: 'slideUpModal 0.3s ease'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            width: '64px',
                            height: '64px',
                            margin: '0 auto 1.25rem',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ef4444'
                        }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="30"
                                height="30"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                <polyline points="16 17 21 12 16 7" />
                                <line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                        </div>

                        <h3 style={{
                            margin: '0 0 0.5rem 0',
                            fontSize: '1.3rem',
                            fontWeight: '700',
                            color: '#1a1a2e'
                        }}>Confirm Logout</h3>

                        <p style={{
                            margin: '0 0 1.75rem 0',
                            color: '#666',
                            fontSize: '0.95rem',
                            lineHeight: '1.5'
                        }}>Are you sure you want to log out? You will need to sign in again to access your account.</p>

                        <div style={{
                            display: 'flex',
                            gap: '0.75rem',
                            justifyContent: 'center'
                        }}>
                            <button
                                className="logout-confirm-btn"
                                onClick={() => setShowLogoutConfirm(false)}
                                style={{
                                    padding: '0.7rem 1.75rem',
                                    borderRadius: '10px',
                                    border: '1.5px solid #ddd',
                                    background: '#ffffff',
                                    color: '#555',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    minWidth: '110px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="logout-confirm-btn"
                                onClick={() => {
                                    setShowLogoutConfirm(false);
                                    handleLogout();
                                }}
                                style={{
                                    padding: '0.7rem 1.75rem',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #d32f2f, #b71c1c)',
                                    color: '#ffffff',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    minWidth: '110px'
                                }}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </aside>
    );
};

export default Sidebar;
